import { logger } from "src/core";
import { MessageManager } from "src/core/streaming/core/messageManager";
import {
  ErrorHandler,
  MetaHandler,
  UpdateHandler
} from "src/core/streaming/core/types";
import { SextantDataSource } from "src/core/streaming/pooledDataSource";
import { Fabric, Node } from "src/gen/schema/models/models";
import {
  AssertDeviceConnectedToCloud,
  BgpNeighborAdvertisedRoute,
  BgpNeighborInfo,
  BgpNeighborReceivedRoute,
  BgpNeighborSummary,
  BgpRouteStats,
  Bmc,
  BmcServerPortInfo,
  Breakout,
  Fabric as FabricType,
  FabricDevice,
  FabricTransaction,
  Fan,
  IpmProcJitterCounters,
  IpmProcLatencyCounters,
  IpmProcLossCounters,
  IpmRawLatencyCounters,
  IpNextHop,
  IpRouteFabricJoinedViewCount,
  IpRouteJoinedView,
  IpRouteJoinedViewCount,
  IpRouteTable,
  IpSlaProbe,
  L2FdbSingle,
  L3Table,
  ManagementConfig,
  ManagementState,
  ManifestRunningImageStatus,
  PluggableConnectionInfo,
  PortChannelMemberState,
  PortChannelState,
  PortConfig,
  PortCounters,
  PortNeighbor,
  ProxyConnectionState,
  ProxyConnectionStats,
  Psu,
  QsfpDomSensor,
  QsfpDomThreshold,
  QsfpInfo,
  RemedyAction,
  ServerFan,
  ServerInterfaceInfo,
  ServerInterfaceMapping,
  ServerIpRouteTable,
  ServerL3Table,
  ServerPortInfo,
  ServerPsu,
  ServerTempSensor,
  ServerToBmc,
  SpanSession,
  StpPortState,
  StpVlanPortState,
  StpVlanState,
  TempSensor,
  VlanVniMap,
  VniMacCounts,
  Vrf,
  VrfEndpointCounts
} from "src/gen/schema/schema/schema";
import {
  SchemaType,
  schemaTypeToJSON
} from "src/gen/schema/schema_path/schema_path";
import { ObjectFilter } from "src/gen/schema/syncfollow/syncfollow";
import {
  AllDeviceAssertsKey,
  AllNetworkAssertKey,
  AllPortAssertsKey,
  DeviceAssert,
  deviceAssertSchemas,
  NetworkAssert,
  networkAssertSchemas,
  PortAssert,
  portAssertSchemas,
  UncategorizedAsserts
} from "src/pages/assertions/assertTypes";
import { stringComparator } from "src/utils/comparator";
import { unreachable } from "src/utils/switch";

import { BaseDataSourceApi } from "./baseDataSourceApi";

export const DEFAULT_LIMIT = 1000;
/* valid Sextant device id xx-xx-xx-xx-xx-xx */
const DEVICE_ID_FORMAT = /^([a-f0-9]{2}-){5}([a-f0-9]{2})$/i;
/* valid Sextant device id de-ad-xx-xx-xx-xx */
const IS_DEAD = /^de-ad-([a-f0-9]{2}-?){4}$/i;

/**
 * Takes a Fabric object and
 * returns all the "Node:deviceIds"
 * in a coma delimited string like
 * xx-xx-xx-xx-xx-xx,xx-xx-xx-xx-xx-xx
 * This is being used so hooks calling
 * Sextant apis have a more stable request
 * @param fabric
 * @param delimiter
 */
export const getSextantDeviceIds = (
  fabric: Fabric | undefined,
  delimiter: string = ","
): string => {
  if (fabric) {
    const nodes = [...fabric.nodes];
    return nodes
      .sort((a: Node, b: Node) => stringComparator(a.deviceId, b.deviceId))
      .map((c: Node) => c.deviceId)
      .join(delimiter);
  }
  return "";
};

export const isValidDeviceId = (
  deviceId: string,
  skipLogging = false
): boolean => {
  if (!DEVICE_ID_FORMAT.test(deviceId)) {
    if (!skipLogging) {
      logger.trace("Device id has wrong format");
    }
    return false;
  }
  if (IS_DEAD.test(deviceId)) {
    if (!skipLogging) {
      logger.info(`Device id ${deviceId} is a temp id`);
    }
    return false;
  }
  return true;
};

class SextantApi extends BaseDataSourceApi<SextantDataSource> {
  initDataSource(): SextantDataSource {
    return new SextantDataSource(window.cnc.sextantPool);
  }

  subscribeFabricVlanVniMap(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<VlanVniMap[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.VlanVniMapType]
      }),
      MessageManager.transformer((s) => s.vlanVniMap),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeFabricMacAddress(
    fabricId: string,
    deviceIds: string[],
    limit: number,
    filters: Partial<ObjectFilter>[],
    onSuccess: UpdateHandler<L2FdbSingle[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create(
        {
          filters: filters,
          switchId: deviceIds,
          types: [SchemaType.L2FdbSingleType]
        },
        limit,
        60
      ),
      MessageManager.transformer((s) => s.l2FdbSingle),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeVniMacCounts(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<VniMacCounts[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        fabricId: [fabricId],
        switchId: deviceIds,
        types: [SchemaType.VniMacCountsType]
      }),
      MessageManager.transformer((s) => s.vniMacCounts),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeVrf(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<Vrf[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.VrfType]
      }),
      MessageManager.transformer((s) => s.vrf),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeVrfMacCounts(
    fabricId: string,
    onSuccess: UpdateHandler<VrfEndpointCounts[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        fabricId: [fabricId],
        types: [SchemaType.VrfEndpointCountsType]
      }),
      MessageManager.transformer((s) => s.vrfEndpointCounts),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeFabricLLDP(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<PortNeighbor[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.PortNeighborType]
      }),
      MessageManager.transformer((s) => s.portNeighbor),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeFabricArpMac(
    fabricId: string,
    deviceIds: string[],
    limit: number,
    filters: Partial<ObjectFilter>[],
    onSuccess: UpdateHandler<L3Table[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create(
        {
          filters: filters,
          switchId: deviceIds,
          types: [SchemaType.L3TableType]
        },
        limit
      ),
      MessageManager.transformer((s) => s.l3Table),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }
  subscribeSwitchPortConfig(
    fabricId: string,
    deviceId: string,
    onSuccess: UpdateHandler<PortConfig[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: [deviceId],
        types: [SchemaType.PortConfigType]
      }),
      MessageManager.transformer((s) => s.portConfig),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeFabricPortConfig(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<PortConfig[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.PortConfigType]
      }),
      MessageManager.transformer((s) => s.portConfig),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeStpPortState(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<StpPortState[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.StpPortStateType]
      }),
      MessageManager.transformer((s) => s.stpPortState),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeStpVlanState(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<StpVlanState[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.StpVlanStateType]
      }),
      MessageManager.transformer((s) => s.stpVlanState),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeStpVlanPortState(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<StpVlanPortState[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.StpVlanPortStateType]
      }),
      MessageManager.transformer((s) => s.stpVlanPortState),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeSwitchFan(
    fabricId: string,
    deviceId: string,
    onSuccess: UpdateHandler<Fan[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: [deviceId],
        types: [SchemaType.FanType]
      }),
      MessageManager.transformer((s) => s.fan),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeServerFan(
    fabricId: string,
    deviceId: string,
    onSuccess: UpdateHandler<ServerFan[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: [deviceId],
        types: [SchemaType.ServerFanType]
      }),
      MessageManager.transformer((s) => s.serverFan),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeSwitchPsu(
    fabricId: string,
    deviceId: string,
    onSuccess: UpdateHandler<Psu[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: [deviceId],
        types: [SchemaType.PsuType]
      }),
      MessageManager.transformer((s) => s.psu),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeSwitchTemp(
    fabricId: string,
    deviceId: string,
    onSuccess: UpdateHandler<TempSensor[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: [deviceId],
        types: [SchemaType.TempSensorType]
      }),
      MessageManager.transformer((s) => s.tempSensor),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeRemedyAction(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<RemedyAction[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        fabricId: [fabricId],
        types: [SchemaType.RemedyActionType]
      }),
      MessageManager.transformer((s) => s.remedyAction),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeProxyConnectionState(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<ProxyConnectionState[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.ProxyConnectionStateType]
      }),
      MessageManager.transformer((s) => s.proxyConnectionState),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeProxyConnectionStats(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<ProxyConnectionStats[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.ProxyConnectionStatsType]
      }),
      MessageManager.transformer((s) => s.proxyConnectionStats),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeIpRouteTableType(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<IpRouteTable[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        fabricId: [fabricId],
        switchId: deviceIds,
        types: [SchemaType.IpRouteTableType]
      }),
      MessageManager.transformer((s) => s.ipRouteTable),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeIpRouteJoinedView(
    fabricId: string,
    deviceIds: string[],
    limit: number,
    filters: Partial<ObjectFilter>[],
    onSuccess: UpdateHandler<IpRouteJoinedView[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create(
        {
          filters,
          fabricId: [fabricId],
          switchId: deviceIds,
          types: [SchemaType.IpRouteJoinedViewType]
        },
        limit
      ),
      MessageManager.transformer((s) => {
        return s.ipRouteJoinedView;
      }),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }
  subscribeIpRouteJoinedViewCount(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<IpRouteJoinedViewCount[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        fabricId: [fabricId],
        switchId: deviceIds,
        types: [SchemaType.IpRouteJoinedViewCountType]
      }),
      MessageManager.transformer((s) => {
        return s.ipRouteJoinedViewCount;
      }),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeIpRouteFabricJoinedViewCount(
    fabricId: string,
    onSuccess: UpdateHandler<IpRouteFabricJoinedViewCount[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        fabricId: [fabricId],
        types: [SchemaType.IpRouteFabricJoinedViewCountType]
      }),
      MessageManager.transformer((s) => {
        return s.ipRouteFabricJoinedViewCount;
      }),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeIpNextHop(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<IpNextHop[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        fabricId: [fabricId],
        switchId: deviceIds,
        types: [SchemaType.IpNextHopType]
      }),
      MessageManager.transformer((s) => s.ipNextHop),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeSwitchPortNeighbor(
    fabricId: string,
    deviceId: string,
    onSuccess: UpdateHandler<PortNeighbor[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: [deviceId],
        types: [SchemaType.PortNeighborType]
      }),
      MessageManager.transformer((s) => s.portNeighbor),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeSwitchPortState(
    fabricId: string,
    deviceId: string,
    onSuccess: UpdateHandler<Breakout[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    // no need to wrap as subscribeMultiSwitchPortState handles it.
    return this.subscribeMultiSwitchPortState(
      fabricId,
      [deviceId],
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeMultiSwitchPortState(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<Breakout[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.BreakoutType]
      }),
      MessageManager.transformer((s) => s.breakout),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeServerPortInfo(
    fabricId: string,
    deviceIds: string,
    onSuccess: UpdateHandler<ServerPortInfo[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: [deviceIds],
        types: [SchemaType.ServerPortInfoType]
      }),
      MessageManager.transformer((s) => s.serverPortInfo),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeSwitchQsfpDomThresholdInfo(
    fabricId: string,
    deviceId: string,
    onSuccess: UpdateHandler<QsfpDomThreshold[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: [deviceId],
        types: [SchemaType.QsfpDomThresholdType]
      }),
      MessageManager.transformer((s) => s.qsfpDomThreshold),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeSwitchPortCounters(
    fabricId: string,
    deviceId: string,
    onSuccess: UpdateHandler<PortCounters[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: [deviceId],
        types: [SchemaType.PortCountersType]
      }),
      MessageManager.transformer((s) => s.portCounters),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeSwitchQsfpInfo(
    fabricId: string,
    deviceId: string,
    onSuccess: UpdateHandler<QsfpInfo[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: [deviceId],
        types: [SchemaType.QsfpInfoType]
      }),
      MessageManager.transformer((s) => s.qsfpInfo),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeBgpNeighborInfo(
    fabricId: string,
    deviceId: string,
    filters: Partial<ObjectFilter>[],
    onSuccess: UpdateHandler<BgpNeighborInfo[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        filters,
        switchId: [deviceId],
        types: [SchemaType.BgpNeighborInfoType]
      }),
      MessageManager.transformer((s) => s.bgpNeighborInfo),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeBgpNeighborInfos(
    fabricId: string,
    deviceId: string[],
    filters: Partial<ObjectFilter>[],
    onSuccess: UpdateHandler<BgpNeighborInfo[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        filters,
        switchId: deviceId,
        types: [SchemaType.BgpNeighborInfoType]
      }),
      MessageManager.transformer((s) => s.bgpNeighborInfo),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeBgpNeighborReceivedRoute(
    fabricId: string,
    deviceId: string,
    filters: Partial<ObjectFilter>[],
    onSuccess: UpdateHandler<BgpNeighborReceivedRoute[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create(
        {
          filters,
          switchId: [deviceId],
          types: [SchemaType.BgpNeighborReceivedRouteType]
        },
        DEFAULT_LIMIT
      ),
      MessageManager.transformer((s) => s.bgpNeighborReceivedRoute),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeBgpNeighborAdvertisedRoute(
    fabricId: string,
    deviceId: string,
    filters: Partial<ObjectFilter>[],
    onSuccess: UpdateHandler<BgpNeighborAdvertisedRoute[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create(
        {
          filters,
          switchId: [deviceId],
          types: [SchemaType.BgpNeighborAdvertisedRouteType]
        },
        DEFAULT_LIMIT
      ),
      MessageManager.transformer((s) => s.bgpNeighborAdvertisedRoute),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeBgpPeerNeighbor(
    fabricId: string,
    deviceId: string[],
    filters: Partial<ObjectFilter>[],
    onSuccess: UpdateHandler<BgpNeighborSummary[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        filters,
        switchId: deviceId,
        types: [SchemaType.BgpNeighborSummaryType]
      }),
      MessageManager.transformer((s) => s.bgpNeighborSummary),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeBgpRouteStats(
    fabricId: string,
    deviceId: string[],
    onSuccess: UpdateHandler<BgpRouteStats[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceId,
        types: [SchemaType.BgpRouteStatsType]
      }),
      MessageManager.transformer((s) => s.bgpRouteStats),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeManifestRunningImage(
    fabricId: string,
    deviceId: string[],
    onSuccess: UpdateHandler<ManifestRunningImageStatus[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceId,
        types: [SchemaType.ManifestRunningImageStatusType]
      }),
      MessageManager.transformer((s) => s.manifestRunningImageStatus),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeSwitchOptics(
    fabricId: string,
    deviceId: string,
    onSuccess: UpdateHandler<QsfpDomSensor[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: [deviceId],
        types: [SchemaType.QsfpDomSensorType]
      }),
      MessageManager.transformer((s) => s.qsfpDomSensor),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeFabricTransactions(
    fabricId: string,
    onSuccess: UpdateHandler<FabricTransaction[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        fabricId: [fabricId],
        types: [SchemaType.FabricTransactionType]
      }),
      MessageManager.transformer((s) => s.fabricTransaction),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribePortChannelState(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<PortChannelState[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.PortChannelStateType]
      }),
      MessageManager.transformer((s) => s.portChannelState),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeServerInterfaceMapping(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<ServerInterfaceMapping[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.ServerInterfaceMappingType]
      }),
      MessageManager.transformer((s) => s.serverInterfaceMapping),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribePortChannelMembersState(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<PortChannelMemberState[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.PortChannelMemberStateType]
      }),
      MessageManager.transformer((s) => s.portChannelMemberState),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeAllDeviceAsserts(
    fabricId: string,
    deviceIds: string[],
    filters: Partial<ObjectFilter>[],
    limit: number = 1000,
    types: SchemaType[] | undefined = deviceAssertSchemas,
    onSuccess: UpdateHandler<DeviceAssert[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ): typeof MessageManager.unsubscribe {
    const req = MessageManager.create(
      {
        filters,
        switchId: deviceIds,
        types
      },
      limit
    );
    const transformer = MessageManager.transformer((series) => {
      const deviceAsserts = schemaTypeToJSON(series.objectType) as
        | AllDeviceAssertsKey
        | UncategorizedAsserts;

      switch (deviceAsserts) {
        case "AssertDeviceConnectedToCloudType":
          return series.assertDeviceConnectedToCloud.length
            ? series.assertDeviceConnectedToCloud
            : [];
        case "AssertDeviceConnectedToFabricType":
          return series.assertDeviceConnectedToFabric.length
            ? series.assertDeviceConnectedToFabric
            : [];
        case "AssertVlanHasTrafficType":
          return series.assertVlanHasTraffic.length
            ? series.assertVlanHasTraffic
            : [];
        case "AssertManagementPortConfigSameType":
          return series.assertManagementPortConfigSame.length
            ? series.assertManagementPortConfigSame
            : [];
        case "AssertDeviceResourceUsageBelowThresholdType":
          return series.assertDeviceResourceUsageBelowThreshold.length
            ? series.assertDeviceResourceUsageBelowThreshold
            : [];
        case "AssertDeviceFanSpeedBelowThresholdType":
          return series.assertDeviceFanSpeedBelowThreshold.length
            ? series.assertDeviceFanSpeedBelowThreshold
            : [];
        case "AssertDeviceTemperatureBelowThresholdType":
          return series.assertDeviceTemperatureBelowThreshold.length
            ? series.assertDeviceTemperatureBelowThreshold
            : [];
        case "AssertDevicePsuUsageBelowThresholdType":
          return series.assertDevicePsuUsageBelowThreshold.length
            ? series.assertDevicePsuUsageBelowThreshold
            : [];
        case "AssertNxosDeviceExpectedFeaturesType":
          return series.assertNxosDeviceExpectedFeatures.length
            ? series.assertNxosDeviceExpectedFeatures
            : [];
        default:
          unreachable(deviceAsserts);
      }
      return [];
    });
    return this.getDataSource().subscribe(
      fabricId,
      req,
      transformer,
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeAllPortAsserts(
    fabricId: string,
    deviceIds: string[],
    filters: Partial<ObjectFilter>[],
    limit: number = 1000,
    types: SchemaType[] | undefined = portAssertSchemas,
    onSuccess: UpdateHandler<PortAssert[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    const req = MessageManager.create(
      {
        switchId: deviceIds,
        types,
        filters
      },
      limit
    );
    const transformer = MessageManager.transformer((series): PortAssert[] => {
      const portAsserts = schemaTypeToJSON(series.objectType) as
        | AllPortAssertsKey
        | UncategorizedAsserts;
      switch (portAsserts) {
        case "AssertPortLinkUpType":
          return series.assertPortLinkUp.length ? series.assertPortLinkUp : [];
        case "AssertPortExpectedNeighborType":
          return series.assertPortExpectedNeighbor.length
            ? series.assertPortExpectedNeighbor
            : [];
        case "AssertPortLinkDownType":
          return series.assertPortLinkDown.length
            ? series.assertPortLinkDown
            : [];
        case "AssertPortConnectionSpeedMatchType":
          return series.assertPortConnectionSpeedMatch.length
            ? series.assertPortConnectionSpeedMatch
            : [];
        case "AssertPortNotConnectedToFabricType":
          return series.assertPortNotConnectedToFabric.length
            ? series.assertPortNotConnectedToFabric
            : [];
        case "AssertPortSpeedConsistentType":
          return series.assertPortSpeedConsistent.length
            ? series.assertPortSpeedConsistent
            : [];
        case "AssertExpectedPortBreakoutExistsType":
          return series.assertExpectedPortBreakoutExists.length
            ? series.assertExpectedPortBreakoutExists
            : [];
        case "AssertSubInterfaceUpType":
          return series.assertSubInterfaceUp.length
            ? series.assertSubInterfaceUp
            : [];
        case "AssertSubInterfaceDownType":
          return series.assertSubInterfaceDown.length
            ? series.assertSubInterfaceDown
            : [];
        case "AssertPortChannelMemberUpType":
          return series.assertPortChannelMemberUp.length
            ? series.assertPortChannelMemberUp
            : [];
        case "AssertPortVlanStpStateConsistentType":
          return series.assertPortVlanStpStateConsistent.length
            ? series.assertPortVlanStpStateConsistent
            : [];
        case "AssertPortNoUnknownVlanFoundType":
          return series.assertPortNoUnknownVlanFound.length
            ? series.assertPortNoUnknownVlanFound
            : [];
        case "AssertPortPluggablePidSameType":
          return series.assertPortPluggablePidSame.length
            ? series.assertPortPluggablePidSame
            : [];
        case "AssertPortQsfpHealthyType":
          return series.assertPortQsfpHealthy.length
            ? series.assertPortQsfpHealthy
            : [];
        default:
          unreachable(portAsserts);
      }
      return [];
    });
    return this.getDataSource().subscribe(
      fabricId,
      req,
      transformer,
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeAllNetworkAsserts(
    fabricId: string,
    deviceIds: string[],
    filters: Partial<ObjectFilter>[],
    limit: number = 1000,
    types: SchemaType[] | undefined = networkAssertSchemas,
    onSuccess: UpdateHandler<NetworkAssert[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    const req = MessageManager.create(
      {
        switchId: deviceIds,
        types,
        filters
      },
      limit
    );

    const transformer = MessageManager.transformer(
      (series): NetworkAssert[] => {
        const networkAsserts = schemaTypeToJSON(series.objectType) as
          | AllNetworkAssertKey
          | UncategorizedAsserts;
        switch (networkAsserts) {
          case "AssertBgpPeerEstablishedType":
            return series.assertBgpPeerEstablished.length
              ? series.assertBgpPeerEstablished
              : [];
          case "AssertStaticDefaultRouteExistsType":
            return series.assertStaticDefaultRouteExists.length
              ? series.assertStaticDefaultRouteExists
              : [];
          case "AssertMultisiteLinkUpType":
            return [];
          case "AssertMultisiteLatencyStableType":
            return [];
          case "AssertMultisiteExpectedMtuType":
            return [];
          default:
            unreachable(networkAsserts);
        }
        return [];
      }
    );

    return this.getDataSource().subscribe(
      fabricId,
      req,
      transformer,
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeFabricType(
    fabricId: string,
    deviceIds: string[],
    filters: Partial<ObjectFilter>[],
    onSuccess: UpdateHandler<FabricType[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    const req = MessageManager.create({
      fabricId: [fabricId],
      switchId: deviceIds,
      types: [SchemaType.FabricType],
      filters
    });

    return this.getDataSource().subscribe(
      fabricId,
      req,
      MessageManager.transformer((s) => s.fabric),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeFabricDeviceType(
    fabricId: string,
    filters: Partial<ObjectFilter>[],
    onSuccess: UpdateHandler<FabricDevice[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    const req = MessageManager.create({
      fabricId: [fabricId],
      types: [SchemaType.FabricDeviceType],
      filters
    });

    return this.getDataSource().subscribe(
      fabricId,
      req,
      MessageManager.transformer((s) => s.fabricDevice),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeIpmLoss(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<IpmProcLossCounters[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.IpmProcLossCountersType]
      }),
      MessageManager.transformer((s) => s.ipmProcLossCounters),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeIpmRawLatency(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<IpmRawLatencyCounters[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.IpmRawLatencyCountersType]
      }),
      MessageManager.transformer((s) => s.ipmRawLatencyCounters),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeIpmJitter(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<IpmProcJitterCounters[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.IpmProcJitterCountersType]
      }),
      MessageManager.transformer((s) => s.ipmProcJitterCounters),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeIpmProcLatency(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<IpmProcLatencyCounters[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.IpmProcLatencyCountersType]
      }),
      MessageManager.transformer((s) => s.ipmProcLatencyCounters),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeManagementState(
    fabricId: string,
    objectIds: Array<string>,
    onSuccess: UpdateHandler<ManagementState[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        objectId: objectIds,
        types: [SchemaType.ManagementStateType]
      }),
      MessageManager.transformer((s) => s.managementState),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeServerPsu(
    fabricId: string,
    deviceId: string,
    onSuccess: UpdateHandler<ServerPsu[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        objectId: [deviceId],
        types: [SchemaType.ServerPsuType]
      }),
      MessageManager.transformer((s) => s.serverPsu),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeServerTemp(
    fabricId: string,
    deviceId: string,
    onSuccess: UpdateHandler<ServerTempSensor[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: [deviceId],
        types: [SchemaType.ServerTempSensorType]
      }),
      MessageManager.transformer((s) => s.serverTempSensor),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeServerRoute(
    fabricId: string,
    deviceId: string,
    filters: Partial<ObjectFilter>[],
    onSuccess: UpdateHandler<ServerIpRouteTable[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: [deviceId],
        types: [SchemaType.ServerIpRouteTableType],
        filters
      }),
      MessageManager.transformer((s) => s.serverIpRouteTable),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeServerMac(
    fabricId: string,
    deviceId: string,
    filters: Partial<ObjectFilter>[],
    onSuccess: UpdateHandler<ServerL3Table[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: [deviceId],
        types: [SchemaType.ServerL3TableType],
        filters
      }),
      MessageManager.transformer((s) => s.serverL3Table),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }
  subscribeServerInterface(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<ServerInterfaceInfo[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.ServerInterfaceInfoType]
      }),
      MessageManager.transformer((s) => s.serverInterfaceInfo),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }
  subscribeManagementConfig(
    fabricId: string,
    objectIds: Array<string>,
    onSuccess: UpdateHandler<ManagementConfig[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        objectId: objectIds,
        types: [SchemaType.ManagementConfigType]
      }),
      MessageManager.transformer((s) => s.managementConfig),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeAssertDeviceConnectedToCloud(
    fabricId: string,
    objectIds: Array<string>,
    onSuccess: UpdateHandler<AssertDeviceConnectedToCloud[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        objectId: objectIds,
        types: [SchemaType.AssertDeviceConnectedToCloudType]
      }),
      MessageManager.transformer((s) => s.assertDeviceConnectedToCloud),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeBmc(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<Bmc[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.BmcType]
      }),
      MessageManager.transformer((s) => s.bmc),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeServerToBmc(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<ServerToBmc[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        switchId: deviceIds,
        types: [SchemaType.ServerToBmcType]
      }),
      MessageManager.transformer((s) => s.serverToBmc),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeBmcServerPortInfo(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<BmcServerPortInfo[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        objectId: deviceIds,
        types: [SchemaType.BmcServerPortInfoType]
      }),
      MessageManager.transformer((s) => s.bmcServerPortInfo),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribePluggableConnectionInfo(
    fabricId: string,
    filters: Partial<ObjectFilter>[],
    onSuccess: UpdateHandler<PluggableConnectionInfo[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        fabricId: [fabricId],
        types: [SchemaType.PluggableConnectionInfoType],
        filters
      }),
      MessageManager.transformer((s) => s.pluggableConnectionInfo),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeIpSlaProbe(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<IpSlaProbe[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        objectId: deviceIds,
        types: [SchemaType.IpSlaProbeType]
      }),
      MessageManager.transformer((s) => s.ipSlaProbe),
      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }

  subscribeSpanSession(
    fabricId: string,
    deviceIds: string[],
    onSuccess: UpdateHandler<SpanSession[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ) {
    return this.getDataSource().subscribe(
      fabricId,
      MessageManager.create({
        objectId: deviceIds,
        types: [SchemaType.SpanSessionType]
      }),
      MessageManager.transformer((s) => s.spanSession),

      MessageManager.reducer,
      onSuccess,
      onError,
      onMeta
    );
  }
}

const Sextant = new SextantApi();
export { Sextant };
